import { komboHalvesOf } from '@agos/shared'
import { ruleIdFromSyntheticId } from '../gift-id'
import { giftNameOf, isGiftItem, type KitchenPart } from './item-name'

/**
 * Which lines of an order the kitchen is shown.
 *
 * A card that lists a Coke wastes a cook's attention; a card that omits a pizza
 * loses the order. So this is the most consequential pure function on the
 * screen, and legacy spells it as one 60-line filter with four fall-throughs.
 * Ported whole and pinned, because every branch is there for an order shape
 * that really exists in the dumps.
 *
 * The rules, in the order they are applied:
 *
 *   0. A **surcharge** — a bottle deposit — is never shown. It is not food, it is
 *      not something anybody makes, and it carries a negative `product_id` that
 *      rule 3 would otherwise cook (§9.217).
 *   1. A **gift** is shown only if its rule says `showInKitchen`. Three ways to
 *      find that rule, because three generations of orders are in the table.
 *   2. **Kombo pizza and kombo box** are always shown, whatever their category.
 *   3. Anything with a **synthetic or zero product id** is always shown: custom
 *      pizzas, lunch menus, and lines rewritten by an order edit.
 *   4. Everything else is shown only if its **category has `containsFood`**.
 */

export interface KitchenLine {
  /** The row's own id, `order_items.id` - what an add-on's `addonOfId` names (§9.713), and what a tap is stored on. */
  id?: number
  /** The order the row is on - the other half of the tap's address, `PATCH /orders/{orderId}/items/{id}/…`. */
  orderId?: number
  /**
   * A cook's tap on this line - `FRONTEND.md` §9.811, BACKEND.md §10A.454, owner O157: `working`, `done`, or null for
   * untouched. Read through `serverProgressOf`, so a value this build does not know draws as untouched.
   */
  kitchenProgress?: string | null
  /**
   * The row this line was ordered with - `BACKEND.md` §10A.366, `FRONTEND.md` §9.713. The card draws it under that
   * line, so the fries belong to burger Bacon and not to burger Sweet. Read through `addonParentOf`, because the
   * generated `OrderListLine` does not declare it until the client is regenerated.
   */
  addonOfId?: number | null
  productId?: number
  productName?: string
  quantity?: number
  giftRuleId?: number
  giftRuleType?: string
  boxGroupId?: string
  boxGroupName?: string
  /**
   * Which course of the day's menu this line came off — `BACKEND.md` §10A.88.
   *
   * Absent on everything else, and that absence is the point: a lunch line and a custom pizza both
   * arrive with a synthetic negative id and no box, so before this the kitchen could not box one
   * without boxing the other.
   */
  lunchMealType?: string | null
  /**
   * The surcharge this line **is** — `BACKEND.md` §10A.89.
   *
   * A deposit is stored as its own row with a negative `product_id`, so without this it is
   * indistinguishable from a custom pizza and the kitchen cooks it.
   */
  surchargeDefinitionId?: number | null
  /** Which doobjednávka this line belongs to; 1 unless the table ordered again. */
  kitchenRound?: number
  /**
   * Whether the waiter has carried it out - Otvorené stoly's *Vyniesť*, `served` or `pending`. On the wire since
   * `OrderDtos.ListLine`; declared here for §9.670, which folds an earlier round once all of it is on the table.
   */
  servedStatus?: string | null
  /**
   * What a composed line was assembled from - `BACKEND.md` §10A.337, owner O51.
   *
   * Empty for every line that is not composed. The card reads it where the name carries no
   * parenthesised list, which is the whole of `FRONTEND.md` §9.667 - a franchise whose builder wrote
   * the name without one had a cook looking at a bare *Vlastná pizza*.
   */
  parts?: KitchenPart[] | null
  /**
   * What somebody typed about this one line for the kitchen - `FRONTEND.md` §9.691, owner #3: *"tie
   * poznámky aby ukazovalo v kuchyni ... to je pre kuchyňu"*. Read through `lineNoteOf`, because the
   * generated `OrderListLine` does not declare it until the client is regenerated.
   */
  note?: string | null
}

export interface KitchenVisibilityMaps {
  /** `gifts/rules` id → showInKitchen. */
  amountGiftRules: Record<number, boolean>
  /** `gifts/product-rules` id → showInKitchen. */
  productGiftRules: Record<number, boolean>
  /** Lower-cased gift name → showInKitchen, for orders that stored no rule id. */
  giftRulesByName: Record<string, boolean>
  /** Product id → its category id. */
  categoryOfProduct: Record<number, number | null>
  /** Category id → `containsFood`. */
  categoryContainsFood: Record<number, boolean>
}

const KOMBO_BOX = 'kombo box'

/**
 * Products created by an edit carry `id * 1000 + n`. The real product is the
 * quotient — falling back to the id itself for products numbered under 1000.
 */
function baseProductId(productId: number): number {
  return Math.floor(productId / 1000) || productId
}

function giftIsVisible(line: KitchenLine, maps: KitchenVisibilityMaps): boolean {
  // Primary: the rule the order itself recorded.
  if (line.giftRuleId != null && line.giftRuleType != null) {
    if (line.giftRuleType === 'amount') return maps.amountGiftRules[line.giftRuleId] === true
    if (line.giftRuleType === 'product') return maps.productGiftRules[line.giftRuleId] === true
  }

  // Secondary: decode the rule out of the synthetic negative id.
  const productId = line.productId ?? null
  if (productId !== null && productId < 0) {
    const ruleId = ruleIdFromSyntheticId(productId)
    if (ruleId > 0) {
      if (maps.amountGiftRules[ruleId] === true || maps.productGiftRules[ruleId] === true) {
        return true
      }
      // The rule exists and says no. Stop here rather than guessing by name.
      if (ruleId in maps.amountGiftRules || ruleId in maps.productGiftRules) return false
    }
  }

  // Tertiary: match by name, for orders taken before rule ids were stored.
  const name = giftNameOf(line.productName).toLowerCase()
  if (name && name in maps.giftRulesByName) return maps.giftRulesByName[name] === true

  // No rule found. A gift nobody can attribute is not a gift the kitchen makes.
  return false
}

export function isKitchenLine(line: KitchenLine, maps: KitchenVisibilityMaps): boolean {
  const name = typeof line.productName === 'string' ? line.productName : ''
  const productId = line.productId ?? null

  // §9.217 — a deposit is charged, printed and refunded; it is never cooked. First because it is the
  // cheapest test, and because *this line is not food at all* reads before *is this gift visible*.
  if (line.surchargeDefinitionId != null) return false

  // A gift is anything flagged by name *or* by a stored rule. Not by a negative
  // id alone — custom pizzas and lunch menus use those too.
  if (isGiftItem(name) || (line.giftRuleId != null && line.giftRuleType != null)) {
    return giftIsVisible(line, maps)
  }

  // §9.430 - the same tolerant reader the card parses with, so one spelling change cannot make a
  // kombo readable on the card and invisible here, or the reverse. **This branch was never the one
  // keeping a web kombo on the board** - rule 3 below catches it by its negative id whatever the
  // name says - which is why the owner could see an unreadable kombo rather than no kombo.
  if (komboHalvesOf(name) !== null) return true
  if (name.toLowerCase().includes(KOMBO_BOX)) return true

  // Custom pizza, lunch menu, or a line rewritten by an edit. Always cooked.
  if (productId !== null && productId <= 0) return true
  if (!productId) return false

  // §9.671 - a known product answers with its own category, `null` included; only an id the menu does
  // not know is read as legacy's size variant. `??` used to treat a known product's `null` as missing
  // and borrow the category of `Math.floor(id / 1000)` - another product's, once ids pass 1 000.
  const categoryId =
    productId in maps.categoryOfProduct
      ? (maps.categoryOfProduct[productId] ?? null)
      : (maps.categoryOfProduct[baseProductId(productId)] ?? null)

  // No category, or the categories have not arrived: hide. The gate is
  // exclusive by design — a drink that slips onto a card is noise a cook has to
  // read past on every order.
  if (categoryId === null || !(categoryId in maps.categoryContainsFood)) return false

  return maps.categoryContainsFood[categoryId] === true
}

export function kitchenLinesOf(
  lines: KitchenLine[] | undefined,
  maps: KitchenVisibilityMaps,
  categoriesReady: boolean,
): KitchenLine[] {
  if (!lines || !categoriesReady) return []
  return lines.filter((line) => isKitchenLine(line, maps))
}

export type ItemEntry =
  | {
      type: 'single'
      line: KitchenLine
      /**
       * §9.713 - the lines ordered with this one (their `addonOfId` is this line's `id`), drawn under it so the cook
       * packs them together. Present only when there is one, so a ticket with no add-ons groups exactly as before.
       */
      addons?: KitchenLine[]
    }
  | { type: 'box'; groupId: string; groupName: string; lines: KitchenLine[] }

/**
 * The row an order line was ordered with, or null - §9.713. Off the untyped row: the generated line types do not
 * declare `addonOfId` until the client is regenerated, and a value that is not a positive whole number is no link.
 */
export function addonParentOf(line: KitchenLine): number | null {
  const value = (line as Record<string, unknown>).addonOfId
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null
}

const DEFAULT_BOX_NAME = 'Burger Box'

/** The day's menu, as one entry — §9.216. */
export const LUNCH_BOX_ID = 'lunch-menu'
// The heading of a kitchen box, drawn where every other group name is drawn — `DEFAULT_BOX_NAME`
// above it is a bare literal for the same reason: the card is printed in Slovak and has no
// translator surface.
// eslint-disable-next-line no-restricted-syntax -- see above
const LUNCH_BOX_NAME = 'Denné menu'

/**
 * Soup before main, whatever order they were tapped in.
 *
 * The keys are the daily menu's own section types. Anything the list does not name keeps the order
 * it arrived in, after the ones it does — a franchise may add a section and the kitchen card should
 * not reorder itself around a name this build has never heard of.
 */
const COURSE_ORDER = ['soup', 'polievka', 'main', 'hlavne', 'dessert', 'dezert']

function courseRank(mealType: string | null | undefined): number {
  const rank = COURSE_ORDER.indexOf((mealType ?? '').toLowerCase())
  return rank === -1 ? COURSE_ORDER.length : rank
}

/**
 * Collapses the lines of a burger box into one entry, in first-appearance
 * order. The box is assembled as a unit, so its slots have to be read as a
 * unit — a cook working down a flat list has no way to know that three of the
 * lines go in the same carton.
 *
 * **The day's menu is boxed the same way — §9.216.** The same sentence is the argument: a soup and a
 * main off one menu are one person's lunch. They are grouped under a single synthetic id rather than
 * per course, because the cart's rule — each meal removable on its own, §9.178 — is about *buying*
 * and a cook is not removing anything.
 *
 * Inside the box the courses are ordered soup-then-main rather than by the order somebody tapped
 * them, which is what `lunchMealType` is for beyond being the discriminator.
 */
export function groupOrderItems(lines: KitchenLine[]): ItemEntry[] {
  const entries: ItemEntry[] = []
  const boxes = new Map<string, Extract<ItemEntry, { type: 'box' }>>()

  /* §9.713 - **an add-on is nested under the line it was ordered with**, so the fries for burger Bacon are packed
     with burger Bacon. Only under a line this list draws as its own entry - a plain line, not a box slot or a lunch
     course - found by row id in a first pass, so the order the two arrive in does not matter. An add-on whose parent
     is not here (hidden as not food, or in another round) is drawn as today, on its own. */
  const parents = new Map<number, Extract<ItemEntry, { type: 'single' }>>()
  for (const line of lines) {
    if (typeof line.id === 'number' && !line.boxGroupId && !line.lunchMealType && addonParentOf(line) === null) {
      parents.set(line.id, { type: 'single', line })
    }
  }

  for (const line of lines) {
    const parentId = addonParentOf(line)
    const parent = parentId === null ? undefined : parents.get(parentId)
    if (parent !== undefined) {
      parent.addons = [...(parent.addons ?? []), line]
      continue
    }
    // §9.216 — a lunch line joins the one lunch box. Checked before `boxGroupId` only in the sense
    // that a lunch line never carries one; a line with both is a box slot first, because that is
    // the group it was assembled in.
    if (!line.boxGroupId && line.lunchMealType) {
      const existingLunch = boxes.get(LUNCH_BOX_ID)
      if (existingLunch) {
        existingLunch.lines.push(line)
        continue
      }
      const lunch = {
        type: 'box' as const,
        groupId: LUNCH_BOX_ID,
        groupName: LUNCH_BOX_NAME,
        lines: [line],
      }
      boxes.set(LUNCH_BOX_ID, lunch)
      entries.push(lunch)
      continue
    }
    if (!line.boxGroupId) {
      entries.push((typeof line.id === 'number' ? parents.get(line.id) : undefined) ?? { type: 'single', line })
      continue
    }
    const existing = boxes.get(line.boxGroupId)
    if (existing) {
      existing.lines.push(line)
      continue
    }
    const entry = {
      type: 'box' as const,
      groupId: line.boxGroupId,
      groupName: line.boxGroupName ?? DEFAULT_BOX_NAME,
      lines: [line],
    }
    boxes.set(line.boxGroupId, entry)
    entries.push(entry)
  }

  const lunch = boxes.get(LUNCH_BOX_ID)
  if (lunch) {
    lunch.lines.sort((left, right) => courseRank(left.lunchMealType) - courseRank(right.lunchMealType))
  }

  return entries
}
